import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { campaignService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Trash2, Loader2 } from "lucide-react";
import { CHANNELS, OBJECTIVES, CAMPAIGN_STATUS_COLOR, CHANNEL_COLOR } from "./constants";

const STATUSES = ["Draft", "Pending Approval", "Approved", "Scheduled", "Running", "Completed", "Paused", "Rejected", "Cancelled"];
const EMPTY = { name: "", description: "", channel: "email", objective: "Marketing", tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function CampaignsListPage() {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await campaignService.list({ status: statusFilter || undefined, channel: channelFilter || undefined, size: 100 });
      setRows(d.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [statusFilter, channelFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (isSuper && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isSuper) delete payload.tenantId;
      const c = await campaignService.create(payload);
      toast.success("Campaign created");
      setOpen(false); navigate(`/admin/campaigns/${c.id}`);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete campaign "${r.name}"?`)) return;
    try { await campaignService.remove(r.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("CAMPAIGN.CREATE");
  const canDelete = hasPermission("CAMPAIGN.DELETE");

  const columns = [
    { key: "name", header: "Campaign", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "channel", header: "Channel", render: (r) => <Badge color={CHANNEL_COLOR[r.channel] || "slate"}>{r.channel}</Badge> },
    { key: "objective", header: "Objective", render: (r) => r.objective || "—" },
    { key: "status", header: "Status", render: (r) => <Badge color={CAMPAIGN_STATUS_COLOR[r.status] || "slate"}>{r.status}</Badge> },
    { key: "audience", header: "Audience", render: (r) => <span className="text-muted-foreground">{r.audienceSnapshot?.eligible ?? 0} eligible</span> },
    { key: "sent", header: "Sent", render: (r) => <span className="text-foreground">{r.stats?.sent ?? 0}</span> },
    {
      key: "_a", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => navigate(`/admin/campaigns/${r.id}`)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`campaign-view-${r.id}`}><Eye className="h-4 w-4" /></button>
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`campaign-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="campaigns-list-page">
      <PageHeader title="Campaigns" subtitle="Consent-aware, approval-gated outbound campaigns"
        actions={canCreate && (
          <button onClick={() => { setForm(EMPTY); setOpen(true); }} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="campaign-create-btn">
            <Plus className="h-4 w-4" /> New Campaign
          </button>
        )}
      />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="campaign-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="campaign-channel-filter">
          <option value="">All channels</option>
          {CHANNELS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <DataTable columns={columns} rows={rows} loading={loading} testid="campaigns-table" empty="No campaigns yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="campaign-dialog">
            <h3 className="text-lg font-bold mb-4">New Campaign</h3>
            <div className="space-y-3.5">
              {isSuper && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="campaign-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="campaign-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Channel"><select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="ciq-input" data-testid="campaign-channel">{CHANNELS.map((c) => <option key={c}>{c}</option>)}</select></Field>
                <Field label="Objective"><select value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="ciq-input" data-testid="campaign-objective">{OBJECTIVES.map((o) => <option key={o}>{o}</option>)}</select></Field>
              </div>
              <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" rows={2} /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="campaign-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create &amp; Build
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
